import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import ExitIntentPopup from '../components/ExitIntentPopup';
import { 
  ArrowRight, 
  Phone,
  Shield,
  Zap,
  BarChart3,
  Bell,
  Globe,
  Check,
  MousePointer,
  Sun,
  Moon
} from 'lucide-react';

const Landing = () => {
  const { language, toggleLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const [showDemo, setShowDemo] = useState(false);

  const features = [
    {
      icon: MousePointer,
      title: language === 'fr' ? 'Exit Intent' : 'Exit Intent',
      description: language === 'fr' 
        ? 'Détection intelligente quand vos visiteurs veulent partir'
        : 'Smart detection when visitors want to leave'
    },
    {
      icon: Zap,
      title: language === 'fr' ? 'Installation rapide' : 'Quick Setup',
      description: language === 'fr'
        ? 'Une seule ligne de code à ajouter'
        : 'Just one line of code to add'
    },
    {
      icon: Bell,
      title: language === 'fr' ? 'Notifications' : 'Notifications',
      description: language === 'fr'
        ? 'Email & Telegram instantanés'
        : 'Instant Email & Telegram'
    },
    {
      icon: Shield,
      title: language === 'fr' ? 'RGPD Compliant' : 'GDPR Compliant',
      description: language === 'fr'
        ? 'Consentement et données sécurisées'
        : 'Consent and secure data'
    },
    {
      icon: BarChart3,
      title: language === 'fr' ? 'Analytics' : 'Analytics',
      description: language === 'fr'
        ? 'Dashboard complet avec statistiques'
        : 'Complete dashboard with statistics'
    },
    {
      icon: Globe,
      title: language === 'fr' ? 'Multilingue' : 'Multilingual',
      description: language === 'fr'
        ? 'Widget en français et anglais'
        : 'Widget in French and English'
    }
  ];

  const stats = [
    { value: '+35%', label: language === 'fr' ? 'Conversions' : 'Conversions' },
    { value: '2min', label: language === 'fr' ? 'Installation' : 'Setup' },
    { value: '24/7', label: language === 'fr' ? 'Disponible' : 'Available' },
    { value: '150+', label: language === 'fr' ? 'Clients' : 'Clients' }
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f] text-white' : 'bg-gray-50 text-gray-900'} overflow-hidden`} style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      
      {/* Background gradient effects */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-[#3272F1]/20 via-blue-500/10 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-blue-600/10 via-cyan-500/5 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-[#3272F1]/5 rounded-full blur-3xl"></div>
        </div>
      )}

      {/* Header */}
      <header className="relative z-50 py-5 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_exitcall/artifacts/v6rf7bvn_logo.svg" 
              alt="HICALL" 
              className="h-10"
            />
          </Link>
          
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors text-sm`}>
              {language === 'fr' ? 'Fonctionnalités' : 'Features'}
            </a>
            <a href="#pricing" className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors text-sm`}>
              {language === 'fr' ? 'Tarifs' : 'Pricing'}
            </a>
            <a href="#demo" className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors text-sm`}>
              {language === 'fr' ? 'Démo' : 'Demo'}
            </a>
          </nav>

          <div className="flex items-center gap-4">
            <button 
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}
              data-testid="theme-toggle"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button 
              onClick={toggleLanguage}
              className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} text-sm transition-colors`}
            >
              {language === 'fr' ? 'EN' : 'FR'}
            </button>
            <Link to="/login" className={`hidden sm:block ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} text-sm transition-colors`}>
              {language === 'fr' ? 'Connexion' : 'Login'}
            </Link>
            <Link to="/register">
              <button className="bg-[#3272F1] hover:bg-[#2560d9] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:shadow-lg hover:shadow-[#3272F1]/25 transition-all" data-testid="header-cta">
                {language === 'fr' ? 'Commencer' : 'Get Started'}
              </button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 pt-16 lg:pt-24 pb-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Text */}
            <div>
              <div className={`inline-flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#3272F1]/10 border-[#3272F1]/20' : 'bg-[#3272F1]/5 border-[#3272F1]/20'} border rounded-full text-sm text-[#3272F1] mb-8`}>
                <Zap className="w-4 h-4" />
                {language === 'fr' ? '30 jours d\'essai gratuit' : '30 days free trial'}
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                {language === 'fr' 
                  ? <>Convertissez vos <span className="text-[#3272F1]">visiteurs</span> en clients</>
                  : <>Convert your <span className="text-[#3272F1]">visitors</span> into customers</>
                }
              </h1>
              
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-10 leading-relaxed max-w-lg`}>
                {language === 'fr'
                  ? 'Hicall détecte quand vos visiteurs veulent partir et leur propose d\'être rappelés. Augmentez vos conversions de +35%.'
                  : 'Hicall detects when visitors want to leave and offers them a callback. Increase your conversions by +35%.'}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link to="/register">
                  <button className="group flex items-center justify-center gap-3 bg-[#3272F1] hover:bg-[#2560d9] text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl hover:shadow-[#3272F1]/25 transition-all" data-testid="hero-cta">
                    {language === 'fr' ? 'Démarrer gratuitement' : 'Start for free'}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
                <button 
                  onClick={() => setShowDemo(true)}
                  className={`flex items-center justify-center gap-2 px-8 py-4 rounded-full font-medium border ${isDark ? 'border-gray-700 hover:border-gray-600 hover:bg-white/5' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-100'} transition-all`}
                  data-testid="demo-btn"
                >
                  {language === 'fr' ? 'Voir la démo' : 'See demo'}
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-6">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl font-bold mb-1">{stat.value}</div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Dashboard Preview */}
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-[#3272F1]/20 rounded-3xl blur-2xl"></div>
              
              {/* Dashboard Card */}
              <div className={`relative ${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-2xl'} rounded-3xl p-2 border`}>
                {/* Browser bar */}
                <div className={`flex items-center gap-2 px-4 py-3 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className={`${isDark ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg px-4 py-1.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} max-w-xs`}>
                      app.hicall.fr/dashboard
                    </div>
                  </div>
                </div>
                
                {/* Dashboard content */}
                <div className="p-6 space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Demandes de rappel' : 'Callback requests'}</h3>
                      <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>Test Company</p>
                    </div>
                    <div className="flex gap-2">
                      <div className="px-4 py-2 bg-[#3272F1]/20 rounded-lg text-xs text-[#3272F1] flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#3272F1] rounded-full"></div>
                        {language === 'fr' ? 'Tester le widget' : 'Test widget'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Stats cards */}
                  <div className="grid grid-cols-4 gap-3">
                    <div className={`${isDark ? 'bg-gray-800/50' : 'bg-gray-50'} rounded-xl p-4`}>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>{language === 'fr' ? 'Total' : 'Total'}</p>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>24</p>
                    </div>
                    <div className={`${isDark ? 'bg-gray-800/50' : 'bg-gray-50'} rounded-xl p-4`}>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>{language === 'fr' ? 'En attente' : 'Pending'}</p>
                      <p className="text-2xl font-bold text-amber-400">8</p>
                    </div>
                    <div className={`${isDark ? 'bg-gray-800/50' : 'bg-gray-50'} rounded-xl p-4`}>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>{language === 'fr' ? 'Complétées' : 'Completed'}</p>
                      <p className="text-2xl font-bold text-[#3272F1]">16</p>
                    </div>
                    <div className={`${isDark ? 'bg-gray-800/50' : 'bg-gray-50'} rounded-xl p-4`}>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-1`}>{language === 'fr' ? '7 jours' : '7 days'}</p>
                      <p className="text-2xl font-bold text-cyan-400">12</p>
                    </div>
                  </div>
                  
                  {/* Table preview */}
                  <div className={`${isDark ? 'bg-gray-800/30' : 'bg-gray-50'} rounded-xl overflow-hidden`}>
                    <div className={`grid grid-cols-4 gap-4 px-4 py-3 text-xs ${isDark ? 'text-gray-500 border-gray-700/50' : 'text-gray-500 border-gray-200'} border-b`}>
                      <span>{language === 'fr' ? 'Téléphone' : 'Phone'}</span>
                      <span>Page</span>
                      <span>{language === 'fr' ? 'Statut' : 'Status'}</span>
                      <span>Date</span>
                    </div>
                    <div className="grid grid-cols-4 gap-4 px-4 py-3 text-xs items-center">
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-1.5`}>
                        <img src="https://flagcdn.com/20x15/fr.png" alt="FR" className="w-5 h-4 rounded-sm object-cover" />
                        +33 6 12 34 56 78
                      </span>
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>example.com/...</span>
                      <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full text-[10px] w-fit">pending</span>
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>21/01/2026</span>
                    </div>
                    <div className={`grid grid-cols-4 gap-4 px-4 py-3 text-xs items-center border-t ${isDark ? 'border-gray-700/30' : 'border-gray-200'}`}>
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-1.5`}>
                        <img src="https://flagcdn.com/20x15/be.png" alt="BE" className="w-5 h-4 rounded-sm object-cover" />
                        +32 4 71 23 45 67
                      </span>
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>example.com/...</span>
                      <span className="px-2 py-1 bg-[#3272F1]/20 text-[#3272F1] rounded-full text-[10px] w-fit">completed</span>
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>20/01/2026</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating widget preview */}
              <div className="absolute -bottom-6 -left-6 w-64 bg-[#18181b] rounded-2xl shadow-2xl p-5 border border-gray-800">
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#3272F1]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Phone className="w-6 h-6 text-[#3272F1]" />
                  </div>
                  <h4 className="font-semibold text-white text-sm mb-1">
                    {language === 'fr' ? 'Avant de partir...' : 'Before you go...'}
                  </h4>
                  <p className="text-xs text-gray-400 mb-3">
                    {language === 'fr' ? 'Être rappelé gratuitement ?' : 'Get a free callback?'}
                  </p>
                  <div className="flex items-center bg-[#27272a] rounded-lg overflow-hidden mb-3 border border-gray-700">
                    <div className="flex items-center gap-1.5 px-2 py-2 bg-[#3f3f46] border-r border-gray-700">
                      <img src="https://flagcdn.com/20x15/fr.png" alt="FR" className="w-5 h-4 rounded-sm object-cover" />
                      <span className="text-xs text-gray-300 font-medium">+33</span>
                      <svg className="w-3 h-3 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>
                    <div className="px-2 py-2 text-gray-500 text-xs flex-1">
                      06 12 34 56 78
                    </div>
                  </div>
                  <div className="bg-[#3272F1] text-white rounded-lg py-2.5 text-xs font-semibold">
                    {language === 'fr' ? 'Être rappelé' : 'Call me back'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative z-10 py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#3272F1] text-sm font-medium mb-4 block">
              {language === 'fr' ? 'FONCTIONNALITÉS' : 'FEATURES'}
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              {language === 'fr' ? 'Tout ce dont vous avez besoin' : 'Everything you need'}
            </h2>
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} max-w-2xl mx-auto`}>
              {language === 'fr' 
                ? 'Un outil complet pour capturer les visiteurs qui veulent partir de votre site'
                : 'A complete tool to capture visitors who want to leave your site'}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map(({ icon: Icon, title, description }, index) => (
              <div 
                key={index}
                className={`group ${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800 hover:border-[#3272F1]/50' : 'bg-white border-gray-200 hover:border-[#3272F1]/50 shadow-sm'} p-6 rounded-2xl border transition-all duration-300`}
              >
                <div className="w-12 h-12 bg-[#3272F1]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#3272F1]/30 transition-colors">
                  <Icon className="w-6 h-6 text-[#3272F1]" />
                </div>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>{title}</h3>
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm leading-relaxed`}>{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="demo" className="relative z-10 py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Steps */}
            <div>
              <span className="text-[#3272F1] text-sm font-medium mb-4 block">
                {language === 'fr' ? 'COMMENT ÇA MARCHE' : 'HOW IT WORKS'}
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold mb-12">
                {language === 'fr' ? 'Simple comme bonjour' : 'Simple as hello'}
              </h2>

              <div className="space-y-8">
                {[
                  {
                    num: '01',
                    title: language === 'fr' ? 'Créez votre compte' : 'Create your account',
                    desc: language === 'fr' ? 'Inscription gratuite en 30 secondes' : 'Free signup in 30 seconds'
                  },
                  {
                    num: '02',
                    title: language === 'fr' ? 'Personnalisez votre widget' : 'Customize your widget',
                    desc: language === 'fr' ? 'Couleurs, textes, position - tout est configurable' : 'Colors, texts, position - everything is configurable'
                  },
                  {
                    num: '03',
                    title: language === 'fr' ? 'Copiez le code' : 'Copy the code',
                    desc: language === 'fr' ? 'Une seule ligne à ajouter sur votre site' : 'Just one line to add to your website'
                  },
                  {
                    num: '04',
                    title: language === 'fr' ? 'Recevez des leads' : 'Receive leads',
                    desc: language === 'fr' ? 'Notifications instantanées par email & Telegram' : 'Instant notifications via email & Telegram'
                  }
                ].map((step, i) => (
                  <div key={i} className="flex gap-6 items-start">
                    <div className="w-12 h-12 bg-[#3272F1] rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold">
                      {step.num}
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>{step.title}</h3>
                      <p className={isDark ? 'text-gray-400' : 'text-gray-600'} style={{ fontSize: '0.875rem' }}>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Code snippet */}
            <div className="relative">
              <div className="absolute inset-0 bg-[#3272F1]/10 rounded-3xl blur-2xl"></div>
              <div className={`relative ${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-lg'} rounded-2xl border overflow-hidden`}>
                <div className={`flex items-center gap-2 px-4 py-3 border-b ${isDark ? 'border-gray-800 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} ml-2`}>index.html</span>
                </div>
                <div className="p-6 font-mono text-sm">
                  <div className={isDark ? 'text-gray-500' : 'text-gray-500'}>&lt;!-- {language === 'fr' ? 'Ajoutez cette ligne avant' : 'Add this line before'} &lt;/body&gt; --&gt;</div>
                  <div className="mt-4">
                    <span className="text-pink-400">&lt;script</span>
                    <span className={isDark ? 'text-white' : 'text-gray-900'}> </span>
                    <span className="text-[#3272F1]">src</span>
                    <span className={isDark ? 'text-white' : 'text-gray-900'}>=</span>
                    <span className="text-amber-300">&quot;https://app.hicall.fr/widget.js&quot;</span>
                  </div>
                  <div className="ml-8">
                    <span className="text-[#3272F1]">data-user-id</span>
                    <span className={isDark ? 'text-white' : 'text-gray-900'}>=</span>
                    <span className="text-amber-300">&quot;YOUR_ID&quot;</span>
                    <span className="text-pink-400">&gt;&lt;/script&gt;</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative z-10 py-24 px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#3272F1] text-sm font-medium mb-4 block">
              {language === 'fr' ? 'TARIFS' : 'PRICING'}
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              {language === 'fr' ? 'Un prix simple et transparent' : 'Simple and transparent pricing'}
            </h2>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              {language === 'fr' ? 'Sans engagement, annulez quand vous voulez' : 'No commitment, cancel anytime'}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Pricing Card */}
            <div className="relative">
              <div className="absolute inset-0 bg-[#3272F1]/20 rounded-3xl blur-xl"></div>
              <div className={`relative ${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-xl'} rounded-3xl p-8 border`}>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>{language === 'fr' ? 'Professionnel' : 'Professional'}</h3>
                    <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>{language === 'fr' ? 'Pour les entreprises qui veulent convertir plus' : 'For businesses that want to convert more'}</p>
                  </div>
                  <div className="px-3 py-1 bg-[#3272F1]/10 border border-[#3272F1]/20 rounded-full text-xs text-[#3272F1]">
                    {language === 'fr' ? 'Populaire' : 'Popular'}
                  </div>
                </div>
                
                <div className="mb-8">
                  <div className="flex items-baseline gap-2">
                    <span className={`text-5xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>9€</span>
                    <span className={isDark ? 'text-gray-500' : 'text-gray-600'}>HT/{language === 'fr' ? 'mois' : 'month'}</span>
                  </div>
                  <p className="text-[#3272F1] text-sm mt-2">{language === 'fr' ? '30 jours d\'essai gratuit inclus' : '30 days free trial included'}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {[
                    { text: language === 'fr' ? 'Demandes de rappel illimitées' : 'Unlimited callback requests', highlight: true },
                    { text: language === 'fr' ? 'Widget entièrement personnalisable' : 'Fully customizable widget', highlight: true },
                    { text: language === 'fr' ? 'Notifications Email & Telegram' : 'Email & Telegram notifications', highlight: true },
                    { text: language === 'fr' ? 'Dashboard avec statistiques' : 'Dashboard with statistics', highlight: false },
                    { text: language === 'fr' ? 'Export CSV des données' : 'CSV data export', highlight: false },
                    { text: language === 'fr' ? 'Support prioritaire' : 'Priority support', highlight: false },
                    { text: language === 'fr' ? 'Conforme RGPD' : 'GDPR compliant', highlight: false }
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className={`w-5 h-5 ${item.highlight ? 'bg-[#3272F1]' : 'bg-[#3272F1]/20'} rounded-full flex items-center justify-center`}>
                        <Check className={`w-3 h-3 ${item.highlight ? 'text-white' : 'text-[#3272F1]'}`} />
                      </div>
                      <span className={item.highlight ? (isDark ? 'text-white font-medium' : 'text-gray-900 font-medium') : (isDark ? 'text-gray-300' : 'text-gray-700')}>{item.text}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/register" className="block">
                  <button className="w-full bg-[#3272F1] hover:bg-[#2560d9] text-white py-4 rounded-full font-semibold hover:shadow-lg hover:shadow-[#3272F1]/25 transition-all" data-testid="pricing-cta">
                    {language === 'fr' ? 'Démarrer l\'essai gratuit' : 'Start free trial'}
                  </button>
                </Link>
                
                <p className={`text-center ${isDark ? 'text-gray-500' : 'text-gray-500'} text-xs mt-4`}>
                  {language === 'fr' ? 'Sans carte bancaire • Annulation facile' : 'No credit card • Easy cancellation'}
                </p>
              </div>
            </div>

            {/* FAQ / Additional Info */}
            <div className="space-y-6">
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-6`}>{language === 'fr' ? 'Questions fréquentes' : 'FAQ'}</h3>
              
              {[
                {
                  q: language === 'fr' ? 'Comment fonctionne l\'essai gratuit ?' : 'How does the free trial work?',
                  a: language === 'fr' 
                    ? 'Vous avez 30 jours pour tester toutes les fonctionnalités sans limite. Aucune carte bancaire n\'est requise pour commencer.'
                    : 'You have 30 days to test all features without limits. No credit card required to start.'
                },
                {
                  q: language === 'fr' ? 'Puis-je annuler à tout moment ?' : 'Can I cancel anytime?',
                  a: language === 'fr'
                    ? 'Oui, vous pouvez annuler votre abonnement à tout moment. Pas de frais cachés ni d\'engagement.'
                    : 'Yes, you can cancel your subscription at any time. No hidden fees or commitment.'
                },
                {
                  q: language === 'fr' ? 'Le widget ralentit-il mon site ?' : 'Does the widget slow down my site?',
                  a: language === 'fr'
                    ? 'Non, le widget est optimisé et pèse moins de 10KB. Il se charge de manière asynchrone.'
                    : 'No, the widget is optimized and weighs less than 10KB. It loads asynchronously.'
                },
                {
                  q: language === 'fr' ? 'Est-ce compatible avec tous les sites ?' : 'Is it compatible with all websites?',
                  a: language === 'fr'
                    ? 'Oui, Hicall fonctionne avec WordPress, Shopify, Wix, et tout site web personnalisé.'
                    : 'Yes, Hicall works with WordPress, Shopify, Wix, and any custom website.'
                }
              ].map((faq, i) => (
                <div key={i} className={`${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200 shadow-sm'} rounded-xl p-5 border`}>
                  <h4 className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium mb-2`}>{faq.q}</h4>
                  <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm leading-relaxed`}>{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials / Social Proof */}
      <section className={`relative z-10 py-24 px-6 lg:px-12 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#3272F1] text-sm font-medium mb-4 block">
              {language === 'fr' ? 'TÉMOIGNAGES' : 'TESTIMONIALS'}
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              {language === 'fr' ? 'Ils nous font confiance' : 'They trust us'}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Marie D.',
                role: language === 'fr' ? 'E-commerce' : 'E-commerce',
                text: language === 'fr' 
                  ? 'Depuis que j\'utilise Hicall, j\'ai récupéré 15% de visiteurs qui allaient partir. Un must-have !'
                  : 'Since using Hicall, I\'ve recovered 15% of visitors who were about to leave. A must-have!'
              },
              {
                name: 'Thomas L.',
                role: language === 'fr' ? 'Agence immobilière' : 'Real estate agency',
                text: language === 'fr'
                  ? 'Installation en 2 minutes, résultats immédiats. Les demandes de rappel ont explosé.'
                  : 'Setup in 2 minutes, immediate results. Callback requests have skyrocketed.'
              },
              {
                name: 'Sophie M.',
                role: language === 'fr' ? 'Coach business' : 'Business coach',
                text: language === 'fr'
                  ? 'Le meilleur investissement pour mon site. Le support est réactif et le produit est top.'
                  : 'The best investment for my site. Support is responsive and the product is great.'
              }
            ].map((testimonial, i) => (
              <div key={i} className={`${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-sm'} rounded-2xl p-6 border`}>
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <svg key={j} className="w-4 h-4 text-[#3272F1]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'} mb-4 leading-relaxed`}>&quot;{testimonial.text}&quot;</p>
                <div>
                  <p className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{testimonial.name}</p>
                  <p className={`${isDark ? 'text-gray-500' : 'text-gray-500'} text-sm`}>{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-24 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-5xl font-bold mb-6">
            {language === 'fr' 
              ? <>Prêt à <span className="text-[#3272F1]">convertir</span> plus ?</>
              : <>Ready to <span className="text-[#3272F1]">convert</span> more?</>
            }
          </h2>
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-10 max-w-2xl mx-auto`}>
            {language === 'fr'
              ? 'Rejoignez les entreprises qui ne laissent plus partir leurs prospects sans rien faire.'
              : 'Join companies that no longer let their prospects leave without taking action.'}
          </p>
          <Link to="/register">
            <button className="group inline-flex items-center gap-3 bg-[#3272F1] hover:bg-[#2560d9] text-white px-10 py-5 rounded-full font-semibold text-lg hover:shadow-xl hover:shadow-[#3272F1]/25 transition-all" data-testid="final-cta">
              {language === 'fr' ? 'Créer mon compte gratuit' : 'Create my free account'}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className={`relative z-10 py-8 px-6 lg:px-12 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_exitcall/artifacts/v6rf7bvn_logo.svg" 
            alt="HICALL" 
            className="h-8"
          />
          <p className={`${isDark ? 'text-gray-500' : 'text-gray-500'} text-sm`}>
            © {new Date().getFullYear()} Hicall. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}
          </p>
        </div>
      </footer>

      {/* Demo Popup */}
      {showDemo && (
        <ExitIntentPopup
          userId="demo"
          settings={{
            popup_title_fr: 'Avant de partir...',
            popup_title_en: 'Before you go...',
            popup_message_fr: 'Souhaitez-vous être rappelé gratuitement ?',
            popup_message_en: 'Would you like a free callback?',
            popup_cta_fr: 'Être rappelé',
            popup_cta_en: 'Call me back',
            popup_legal_fr: 'J\'accepte d\'être recontacté par téléphone.',
            popup_legal_en: 'I agree to be contacted by phone.',
            primary_color: '#3272F1'
          }}
          language={language}
          onClose={() => setShowDemo(false)}
          forceShow={true}
        />
      )}
    </div>
  );
};

export default Landing;
